/**
 * Modèles d'allocations cibles selon le profil de risque
 */
export const RISK_PROFILES = {
  Prudent: {
    label: "Prudent / Défensif",
    description: "Priorité à la préservation du capital et à la liquidité. Faible volatilité acceptée.",
    color: "#0088FE",
    target: {
      liquidites: 45,
      placements: 20,
      immobilier: 30,
      alternatif: 5,
    }
  },
  Equilibre: {
    label: "Équilibré",
    description: "Recherche de performance modérée avec une volatilité maîtrisée sur le moyen terme.",
    color: "#00C49F",
    target: {
      liquidites: 20,
      placements: 35,
      immobilier: 30,
      alternatif: 15,
    }
  },
  Dynamique: {
    label: "Dynamique",
    description: "Recherche d'appréciation du capital sur le long terme. Volatilité élevée acceptée.",
    color: "#FFBB28",
    target: {
      liquidites: 10,
      placements: 45,
      immobilier: 20,
      alternatif: 25,
    }
  },
  Offensif: {
    label: "Offensif / Conviction",
    description: "Forte exposition aux actifs de croissance et non cotés (Private Equity, Club deals).",
    color: "#FF8042",
    target: {
      liquidites: 5,
      placements: 40,
      immobilier: 15,
      alternatif: 40,
    }
  }
};

/**
 * Normalise et extrait les données patrimoniales d'une famille ou société
 */
export function extractClientAssets(client, treeData = null, clientType = "famille") {
  if (!client) {
    return {
      liquidites: { compteCourant: 15000, livrets: 25000, pelCel: 10000, autres: 0 },
      placements: { assuranceVieEuros: 120000, assuranceVieUC: 60000, pea: 45000, pee: 0, compteTitre: 30000, cryptoMetaux: 5000 },
      immobilier: { residencePrincipale: 450000, residenceSecondaire: 0, locatif: 180000, scpi: 40000, dettesImmo: 220000 },
      alternatif: { privateEquity: 50000, dettePrivee: 30000, participationsHipparchus: 45000 },
      professionnel: { valorisationParts: 0, compteCourantAssocie: 0 },
      profilRisque: "Equilibre",
      horizonAnnees: 8
    };
  }

  const isSociete = clientType === "societe" || Boolean(client.formeJuridique || client.siren || client.type === "societe");

  if (isSociete) {
    let treeFilialesVal = 0;
    if (treeData && Array.isArray(treeData.filiales)) {
      treeData.filiales.forEach(f => {
        treeFilialesVal += Number(f.valorisationEstimee || f.valeurEstimee || f.valorisation) || 0;
      });
    }

    const liquidites = {
      compteCourant: Number(client.tresorerieDisponible ?? client.liquidites?.compteCourant ?? client.liquidites) || 0,
      livrets: 0,
      pelCel: 0,
      autres: 0,
    };

    const placements = {
      assuranceVieEuros: 0,
      assuranceVieUC: 0,
      pea: 0,
      pee: 0,
      compteTitre: Number(client.placementsEntreprise ?? client.placements?.compteTitre) || 0,
      cryptoMetaux: 0,
    };

    const immo = client.immobilier || {};
    const immobilier = {
      residencePrincipale: 0,
      residenceSecondaire: 0,
      locatif: Number(immo.locatif ?? client.patrimoineImmobilier) || 0,
      scpi: Number(immo.scpi) || 0,
      dettesImmo: Number(client.dettes ?? client.endettement) || 0,
    };

    const alt = client.alternatif || {};
    const alternatif = {
      privateEquity: Number(alt.privateEquity) || 0,
      dettePrivee: Number(alt.dettePrivee) || 0,
      participationsHipparchus: treeFilialesVal || Number(alt.participationsHipparchus) || 0,
    };

    const pro = client.professionnel || {};
    const professionnel = {
      valorisationParts: Number(client.valorisationEstimee ?? client.valorisation ?? pro.valorisationParts ?? client.patrimoineProfessionnel) || 0,
      compteCourantAssocie: Number(client.gerant?.compteCourantAssocie ?? client.compteCourantAssocie ?? pro.compteCourantAssocie) || 0,
    };

    let profil = client.profilInvestisseur || "Equilibre";
    if (profil && typeof profil === "string") {
      if (profil.toLowerCase().includes("prudent")) profil = "Prudent";
      else if (profil.toLowerCase().includes("dynam")) profil = "Dynamique";
      else if (profil.toLowerCase().includes("agress") || profil.toLowerCase().includes("offens")) profil = "Offensif";
      else profil = "Equilibre";
    } else {
      profil = "Equilibre";
    }

    return {
      liquidites,
      placements,
      immobilier,
      alternatif,
      professionnel,
      profilRisque: profil,
      horizonAnnees: parseInt(client.horizonInvestissement) || 5
    };
  }

  // Agrégation des portefeuilles et participations depuis l'arbre patrimonial
  let treeAv = 0;
  let treePea = 0;
  let treeCto = 0;
  let treeCrypto = 0;
  let treeParts = 0;

  if (treeData && Array.isArray(treeData.personnes)) {
    treeData.personnes.forEach(personne => {
      if (Array.isArray(personne.portefeuilles)) {
        personne.portefeuilles.forEach(ptf => {
          const val = Number(ptf.valorisation) || 0;
          const type = (ptf.type || "").toLowerCase();
          const nom = (ptf.nom || "").toLowerCase();

          if (type.includes("assurance") || nom.includes("assurance")) {
            treeAv += val;
          } else if (type.includes("pea") || nom.includes("pea")) {
            treePea += val;
          } else if (type.includes("titre") || type.includes("cto") || nom.includes("titre") || nom.includes("cto")) {
            treeCto += val;
          } else if (type.includes("crypto") || nom.includes("crypto")) {
            treeCrypto += val;
          } else {
            treeAv += val;
          }
        });
      }

      if (Array.isArray(personne.participations)) {
        personne.participations.forEach(part => {
          treeParts += Number(part.valeurEstimee) || 0;
        });
      }
    });
  }

  // 1. Liquidités
  const liq = client.liquidites || {};
  const liquidites = {
    compteCourant: Number(liq.compteCourant) || Number(client.liquidites) || 0,
    livrets: Number(liq.livrets) || 0,
    pelCel: Number(liq.pelCel) || 0,
    autres: Number(liq.autres) || 0,
  };

  // 2. Placements
  const plc = client.placements || {};
  let avEuros = 0;
  let avUC = 0;
  if (Array.isArray(plc.assuranceVie)) {
    plc.assuranceVie.forEach(av => {
      const mt = Number(av.montant) || 0;
      if (av.type === 'UC') avUC += mt;
      else avEuros += mt;
    });
  } else if (plc.assuranceVie) {
    avEuros = Number(plc.assuranceVie) || 0;
  }

  // Fallback direct sur patrimoineFinancier si aucune assurance-vie n'est renseignée
  const fallbackPatrimoineFinancier = Number(client.patrimoineFinancier) || 0;

  const placements = {
    assuranceVieEuros: avEuros || treeAv || Number(plc.assuranceVieEuros) || fallbackPatrimoineFinancier,
    assuranceVieUC: avUC || Number(plc.assuranceVieUC) || 0,
    pea: Number(plc.pea) || treePea || 0,
    pee: Number(plc.pee) || 0,
    compteTitre: Number(plc.compteTitre) || treeCto || 0,
    cryptoMetaux: Number(plc.cryptoMetaux) || treeCrypto || 0,
  };

  // 3. Immobilier
  const immo = client.immobilier || {};
  let locatifVal = 0;
  let dettesVal = Number(client.endettement) || 0;

  if (client.investissementLocatif?.investissements && Array.isArray(client.investissementLocatif.investissements)) {
    client.investissementLocatif.investissements.forEach(inv => {
      locatifVal += (Number(inv.valeurEstimee) || Number(inv.valeurAchat) || 0);
      dettesVal += (Number(inv.capitalRestantDu) || 0);
    });
  }

  const immobilier = {
    residencePrincipale: Number(immo.residencePrincipale) || Number(client.patrimoineImmobilier) || 0,
    residenceSecondaire: Number(immo.residenceSecondaire) || 0,
    locatif: locatifVal || Number(immo.locatif) || 0,
    scpi: Number(immo.scpi) || 0,
    dettesImmo: dettesVal,
  };

  // 4. Alternatifs
  let partHipparchus = 0;
  if (Array.isArray(client.transactionParticipations)) {
    client.transactionParticipations.forEach(tp => {
      partHipparchus += (Number(tp.committedAmount) || Number(tp.investedAmount) || 0);
    });
  }

  const alt = client.alternatif || {};
  const alternatif = {
    privateEquity: Number(alt.privateEquity) || 0,
    dettePrivee: Number(alt.dettePrivee) || 0,
    participationsHipparchus: partHipparchus || Number(alt.participationsHipparchus) || treeParts || 0,
  };

  // 5. Professionnel
  const pro = client.professionnel || {};
  const professionnel = {
    valorisationParts: Number(pro.valorisationParts) || Number(client.patrimoineProfessionnel) || 0,
    compteCourantAssocie: Number(pro.compteCourantAssocie) || 0,
  };

  // Normalisation du profil de risque
  let profil = client.profilInvestisseur || "Equilibre";
  if (profil.toLowerCase().includes("prudent")) profil = "Prudent";
  else if (profil.toLowerCase().includes("dynam")) profil = "Dynamique";
  else if (profil.toLowerCase().includes("agress") || profil.toLowerCase().includes("offens")) profil = "Offensif";
  else profil = "Equilibre";

  return {
    liquidites,
    placements,
    immobilier,
    alternatif,
    professionnel,
    profilRisque: profil,
    horizonAnnees: parseInt(client.horizonInvestissement) || 8
  };
}

/**
 * Calcule la synthèse de l'allocation et les totaux
 */
export function computeAllocationSummary(assetData, targetProfileKey = "Equilibre") {
  // Totaux par catégorie
  const totalLiquidites = Object.values(assetData.liquidites || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const totalPlacements = Object.values(assetData.placements || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  
  const immoData = assetData.immobilier || {};
  const totalImmoBrut = (Number(immoData.residencePrincipale) || 0) + 
    (Number(immoData.residenceSecondaire) || 0) + 
    (Number(immoData.locatif) || 0) + 
    (Number(immoData.scpi) || 0);
  const totalDettes = Number(immoData.dettesImmo) || 0;

  const totalAlternatif = Object.values(assetData.alternatif || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const totalProfessionnel = Object.values(assetData.professionnel || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  const totalActifsBruts = totalLiquidites + totalPlacements + totalImmoBrut + totalAlternatif + totalProfessionnel;
  const patrimoineNet = Math.max(0, totalActifsBruts - totalDettes);

  // Pourcentages actuels (sur total brut)
  const pctLiquidites = totalActifsBruts > 0 ? (totalLiquidites / totalActifsBruts) * 100 : 0;
  const pctPlacements = totalActifsBruts > 0 ? (totalPlacements / totalActifsBruts) * 100 : 0;
  const pctImmobilier = totalActifsBruts > 0 ? (totalImmoBrut / totalActifsBruts) * 100 : 0;
  const pctAlternatif = totalActifsBruts > 0 ? ((totalAlternatif + totalProfessionnel) / totalActifsBruts) * 100 : 0;

  const currentPercentages = {
    liquidites: Math.round(pctLiquidites * 10) / 10,
    placements: Math.round(pctPlacements * 10) / 10,
    immobilier: Math.round(pctImmobilier * 10) / 10,
    alternatif: Math.round(pctAlternatif * 10) / 10,
  };

  // Comparaison avec allocation cible
  const targetConfig = RISK_PROFILES[targetProfileKey] || RISK_PROFILES.Equilibre;
  const target = targetConfig.target;

  const gapPercentages = {
    liquidites: Math.round((currentPercentages.liquidites - target.liquidites) * 10) / 10,
    placements: Math.round((currentPercentages.placements - target.placements) * 10) / 10,
    immobilier: Math.round((currentPercentages.immobilier - target.immobilier) * 10) / 10,
    alternatif: Math.round((currentPercentages.alternatif - target.alternatif) * 10) / 10,
  };

  // Écarts en euros
  const gapEuros = {
    liquidites: Math.round(totalActifsBruts * (gapPercentages.liquidites / 100)),
    placements: Math.round(totalActifsBruts * (gapPercentages.placements / 100)),
    immobilier: Math.round(totalActifsBruts * (gapPercentages.immobilier / 100)),
    alternatif: Math.round(totalActifsBruts * (gapPercentages.alternatif / 100)),
  };

  // Rendement annuel moyen pondéré estimé
  const yieldEstimates = {
    liquidites: 0.025, // 2.5% livrets/monétaire
    placements: 0.055, // 5.5% fonds mixte / actions
    immobilier: 0.045, // 4.5% locatif / SCPI
    alternatif: 0.090, // 9.0% private equity / dette privée
  };

  const weightedYield = totalActifsBruts > 0 ? (
    (totalLiquidites * yieldEstimates.liquidites) +
    (totalPlacements * yieldEstimates.placements) +
    (totalImmoBrut * yieldEstimates.immobilier) +
    ((totalAlternatif + totalProfessionnel) * yieldEstimates.alternatif)
  ) / totalActifsBruts : 0;

  // Données pour Recharts
  const chartData = [
    { name: "Liquidités & Monétaire", value: totalLiquidites, color: "#0088FE", pct: currentPercentages.liquidites, targetPct: target.liquidites },
    { name: "Placements Financiers", value: totalPlacements, color: "#00C49F", pct: currentPercentages.placements, targetPct: target.placements },
    { name: "Immobilier Brut", value: totalImmoBrut, color: "#FFBB28", pct: currentPercentages.immobilier, targetPct: target.immobilier },
    { name: "Alternatifs & Private Equity", value: totalAlternatif + totalProfessionnel, color: "#8B5CF6", pct: currentPercentages.alternatif, targetPct: target.alternatif },
  ].filter(d => d.value > 0);

  return {
    totalLiquidites,
    totalPlacements,
    totalImmoBrut,
    totalDettes,
    totalAlternatif: totalAlternatif + totalProfessionnel,
    totalActifsBruts,
    patrimoineNet,
    currentPercentages,
    targetPercentages: target,
    gapPercentages,
    gapEuros,
    weightedYield: Math.round(weightedYield * 1000) / 10, // ex: 4.8%
    chartData,
    targetConfig
  };
}

/**
 * Génère des recommandations de rééquilibrage automatisées
 */
export function generateRebalancingAdvice(summary) {
  const { gapEuros, gapPercentages } = summary;
  const advices = [];

  if (gapPercentages.liquidites > 5) {
    advices.push({
      type: "SURPONDERATION",
      category: "Liquidités",
      amount: Math.abs(gapEuros.liquidites),
      severity: "warning",
      message: `Excédent de liquidités (+${gapPercentages.liquidites}% par rapport à la cible, soit environ ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.abs(gapEuros.liquidites))}). Cette trésorerie subit l'érosion monétaire et pourrait être redéployée vers des placements financiers ou de la dette privée.`
    });
  } else if (gapPercentages.liquidites < -5) {
    advices.push({
      type: "SOUSPONDERATION",
      category: "Liquidités",
      amount: Math.abs(gapEuros.liquidites),
      severity: "info",
      message: `Poche de liquidité de précaution sous-dimensionnée (-${Math.abs(gapPercentages.liquidites)}%). Recommandation de reconstituer un coussin de sécurité d'au moins 3 à 6 mois de dépenses courantes.`
    });
  }

  if (gapPercentages.alternatif < -5) {
    advices.push({
      type: "SOUSPONDERATION",
      category: "Alternatifs",
      amount: Math.abs(gapEuros.alternatif),
      severity: "success",
      message: `Sous-exposition en actifs alternatifs / Private Equity (-${Math.abs(gapPercentages.alternatif)}%, soit ~${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.abs(gapEuros.alternatif))}). Opportunité d'arbitrage vers des Club Deals ou solutions de co-investissement Hipparchus offrant une décorrélation des marchés cotés.`
    });
  }

  if (gapPercentages.immobilier > 10) {
    advices.push({
      type: "CONCENTRATION",
      category: "Immobilier",
      amount: Math.abs(gapEuros.immobilier),
      severity: "warning",
      message: `Forte concentration sur l'immobilier physique (+${gapPercentages.immobilier}%). Attention au risque d'illiquidité et à la fiscalité des revenus fonciers / IFI.`
    });
  }

  if (advices.length === 0) {
    advices.push({
      type: "EQUILIBRE",
      category: "Global",
      amount: 0,
      severity: "success",
      message: "L'allocation d'actifs actuelle est en excellente adéquation avec le profil de risque sélectionné."
    });
  }

  return advices;
}

/**
 * Calcule le statut de dérive et les signaux d'alerte pour la gouvernance d'un dossier
 */
export function computeClientDriftStatus(summary, targetProfileKey = "Equilibre") {
  const { gapPercentages, currentPercentages, totalActifsBruts, totalLiquidites, totalPlacements } = summary;

  const gaps = [
    Math.abs(gapPercentages.liquidites || 0),
    Math.abs(gapPercentages.placements || 0),
    Math.abs(gapPercentages.immobilier || 0),
    Math.abs(gapPercentages.alternatif || 0)
  ];
  const maxDrift = Math.max(...gaps);

  let status = "ALIGNED";
  let statusLabel = "Aligné";
  let badgeVariant = "success";

  if (maxDrift > 10) {
    status = "CRITICAL_DRIFT";
    statusLabel = "Dérive critique";
    badgeVariant = "danger";
  } else if (maxDrift > 5) {
    status = "MODERATE_DRIFT";
    statusLabel = "Dérive modérée";
    badgeVariant = "warning";
  }

  const tags = [];
  if (gapPercentages.liquidites > 8 || currentPercentages.liquidites > 25) {
    tags.push({ code: "OVER_LIQUID", label: "Surliquidité", severity: "info" });
  }
  if (gapPercentages.alternatif < -5) {
    tags.push({ code: "UNDER_ALTERNATIVE", label: "Sous-exposé Alternatif", severity: "purple" });
  }
  if (gapPercentages.immobilier > 10) {
    tags.push({ code: "CONCENTRATED_IMMO", label: "Sur-exposition Immo", severity: "warning" });
  }

  const liquidityRatio = totalActifsBruts > 0
    ? Math.round(((totalLiquidites + totalPlacements) / totalActifsBruts) * 1000) / 10
    : 0;

  return {
    maxDrift: Math.round(maxDrift * 10) / 10,
    status,
    statusLabel,
    badgeVariant,
    tags,
    liquidityRatio
  };
}

/**
 * Calcule les métriques globales et la matrice de dérive du cabinet
 */
export function computeCabinetAllocationSummary(familles = [], societes = []) {
  let cabinetAum = 0;
  let cabinetLiquidites = 0;
  let cabinetPlacements = 0;
  let cabinetImmobilier = 0;
  let cabinetAlternatif = 0;

  const dossiers = [];

  // Traitement des familles
  (familles || []).forEach(f => {
    const assets = extractClientAssets(f, null, "famille");
    const summary = computeAllocationSummary(assets, assets.profilRisque);
    const drift = computeClientDriftStatus(summary, assets.profilRisque);

    cabinetAum += summary.patrimoineNet;
    cabinetLiquidites += summary.totalLiquidites;
    cabinetPlacements += summary.totalPlacements;
    cabinetImmobilier += summary.totalImmoBrut;
    cabinetAlternatif += summary.totalAlternatif;

    dossiers.push({
      id: f.id || f._id,
      name: f.nom || f.nomFamille || f.displayName || "Famille sans nom",
      reference: f.reference || f.referenceAudit || null,
      type: "famille",
      aum: summary.patrimoineNet,
      profilRisque: assets.profilRisque,
      currentPercentages: summary.currentPercentages,
      maxDrift: drift.maxDrift,
      status: drift.status,
      statusLabel: drift.statusLabel,
      badgeVariant: drift.badgeVariant,
      tags: drift.tags,
      summary,
      rawClient: f
    });
  });

  // Traitement des sociétés
  (societes || []).forEach(s => {
    const assets = extractClientAssets(s, null, "societe");
    const summary = computeAllocationSummary(assets, assets.profilRisque);
    const drift = computeClientDriftStatus(summary, assets.profilRisque);

    cabinetAum += summary.patrimoineNet;
    cabinetLiquidites += summary.totalLiquidites;
    cabinetPlacements += summary.totalPlacements;
    cabinetImmobilier += summary.totalImmoBrut;
    cabinetAlternatif += summary.totalAlternatif;

    dossiers.push({
      id: s.id || s._id,
      name: s.denomination || s.raisonSociale || s.displayName || "Société",
      reference: s.siren || s.formeJuridique || null,
      type: "societe",
      aum: summary.patrimoineNet,
      profilRisque: assets.profilRisque,
      currentPercentages: summary.currentPercentages,
      maxDrift: drift.maxDrift,
      status: drift.status,
      statusLabel: drift.statusLabel,
      badgeVariant: drift.badgeVariant,
      tags: drift.tags,
      summary,
      rawClient: s
    });
  });

  const totalBrut = cabinetLiquidites + cabinetPlacements + cabinetImmobilier + cabinetAlternatif;

  const cabinetPercentages = {
    liquidites: totalBrut > 0 ? Math.round((cabinetLiquidites / totalBrut) * 1000) / 10 : 0,
    placements: totalBrut > 0 ? Math.round((cabinetPlacements / totalBrut) * 1000) / 10 : 0,
    immobilier: totalBrut > 0 ? Math.round((cabinetImmobilier / totalBrut) * 1000) / 10 : 0,
    alternatif: totalBrut > 0 ? Math.round((cabinetAlternatif / totalBrut) * 1000) / 10 : 0,
  };

  const criticalDriftCount = dossiers.filter(d => d.status === "CRITICAL_DRIFT").length;
  const moderateDriftCount = dossiers.filter(d => d.status === "MODERATE_DRIFT").length;
  const alignedCount = dossiers.filter(d => d.status === "ALIGNED").length;
  const overLiquidCount = dossiers.filter(d => d.tags.some(t => t.code === "OVER_LIQUID")).length;

  return {
    cabinetAum,
    cabinetLiquidites,
    cabinetPlacements,
    cabinetImmobilier,
    cabinetAlternatif,
    cabinetPercentages,
    totalDossiers: dossiers.length,
    criticalDriftCount,
    moderateDriftCount,
    alignedCount,
    overLiquidCount,
    dossiers
  };
}

